import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { TaxOffice } from '../api';
import type { Dictionary } from '../i18n';

/**
 * Wybór urzędu skarbowego z listy zamiast wpisywania kodu z pamięci (E8-S2).
 *
 * Kod z pamięci to prosta droga do deklaracji w złym urzędzie — nasza własna podpowiedź
 * podawała jako przykład 1471, czyli urząd wyspecjalizowany dla największych podatników.
 * Tu wpisuje się miasto albo fragment nazwy, a zapisuje się kod.
 *
 * Słownik ma czterysta wierszy, więc filtrowanie jest w przeglądarce, przy każdej literze.
 * Bez polskich znaków i wielkości liter: „mokotow” ma znaleźć Mokotów, bo tak się pisze
 * na klawiaturze bez polskiego układu.
 */

const MALE_SLOWA = new Set(['w', 'we', 'i', 'na', 'nad', 'pod', 'przy']);

/** „URZĄD SKARBOWY W BIELSKU-BIAŁEJ” → „Urząd Skarbowy w Bielsku-Białej”. */
export function officeLabel(name: string): string {
  return name
    .toLocaleLowerCase('pl')
    .split(' ')
    .map((word) =>
      MALE_SLOWA.has(word)
        ? word
        : word
            .split('-')
            .map((part) => part.charAt(0).toLocaleUpperCase('pl') + part.slice(1))
            .join('-'),
    )
    .join(' ');
}

/** Bez wielkości liter i znaków diakrytycznych; „ł” nie rozkłada się przez NFD, stąd osobno. */
export function normalise(text: string): string {
  return text
    .toLocaleLowerCase('pl')
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Rdzeń nazwy miasta do dopasowania odmienionej formy: „Bolesławiec” → „boleslaw”, które
 * siedzi w „W BOLESŁAWCU”. To służy tylko kolejności podpowiedzi, nie rozstrzyga o niczym.
 */
function cityStem(city: string): string {
  const base = normalise(city.trim());
  return base.slice(0, Math.max(4, base.length - 3));
}

export function matchOffices(offices: TaxOffice[], query: string, city: string | null, limit = 8): TaxOffice[] {
  const tokens = normalise(query).split(/\s+/).filter(Boolean);
  const stem = city ? cityStem(city) : null;
  const inCity = (office: TaxOffice) => (stem && stem.length >= 4 ? normalise(office.name).includes(stem) : false);

  const candidates = offices.filter((office) => {
    const haystack = `${office.code} ${normalise(office.name)}`;
    return tokens.every((token) => haystack.includes(token));
  });
  // Bez zapytania podpowiadamy urzędy z miasta firmy — to one są najczęściej właściwe
  const pool = tokens.length === 0 ? candidates.filter(inCity) : candidates;

  return pool
    .sort((a, b) => {
      const cityDiff = Number(inCity(b)) - Number(inCity(a));
      if (cityDiff !== 0) return cityDiff;
      // Zwykły urząd przed wyspecjalizowanym: mała firma nie ma czego szukać w tym drugim
      const specDiff = Number(a.specialised) - Number(b.specialised);
      if (specDiff !== 0) return specDiff;
      return a.name.localeCompare(b.name, 'pl');
    })
    .slice(0, limit);
}

export function TaxOfficePicker({
  offices,
  value,
  city,
  t,
  id,
  onChange,
}: {
  offices: TaxOffice[];
  /** Kod wybranego urzędu; null, gdy nic nie wybrano. */
  value: string | null;
  /** Miasto firmy — jego urzędy idą na początek listy. */
  city: string | null;
  t: Dictionary;
  id: string;
  onChange: (code: string | null) => void;
}) {
  /* null znaczy „nie edytuję” — pole pokazuje wtedy wybrany urząd, a nie to, co ktoś wpisał */
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  /*
   * Lista jest pozycjonowana względem okna, nie pola: ramka aplikacji ma overflow: hidden,
   * a to pole bywa ostatnie na stronie — lista otwarta „w dół" pod polem zostałaby ucięta.
   * Mierzymy prostokąt pola przy otwarciu; przewinięcie zamyka listę, zamiast ją gonić.
   */
  const [place, setPlace] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const selected = useMemo(() => offices.find((office) => office.code === value) ?? null, [offices, value]);
  const options = useMemo(() => matchOffices(offices, query ?? '', city), [offices, query, city]);

  useEffect(() => {
    setActive(0);
  }, [options]);

  useLayoutEffect(() => {
    if (!open || !input.current) return;
    const rect = input.current.getBoundingClientRect();
    setPlace({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(120, Math.min(320, window.innerHeight - rect.bottom - 16)),
    });
  }, [open]);

  // Kliknięcie poza polem, przewinięcie albo zmiana rozmiaru zamykają listę i przywracają wybrany urząd
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close();
    };
    // Przewinięcie samej listy (ma własny pasek) nie jest powodem, żeby ją zamykać
    const shut = (event: Event) => {
      if (event.type === 'scroll' && event.target instanceof Node && root.current?.contains(event.target)) return;
      close();
    };
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('scroll', shut, true);
      window.removeEventListener('resize', shut);
    };
  });

  function close() {
    setOpen(false);
    setQuery(null);
  }

  function pick(office: TaxOffice) {
    onChange(office.code);
    close();
  }

  const shown = query ?? (selected ? `${selected.code} — ${officeLabel(selected.name)}` : '');

  return (
    <div className="combo" ref={root}>
      <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
        <input
          id={id}
          ref={input}
          className="inp"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && options[active] ? `${listId}-${options[active].code}` : undefined}
          autoComplete="off"
          placeholder={t.filing.taxOfficeSearch}
          value={shown}
          onFocus={() => {
            setQuery(selected ? '' : (query ?? ''));
            setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActive((index) => Math.min(index + 1, options.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              if (!open) return;
              event.preventDefault();
              // Sam czterocyfrowy kod też ma działać — księgowa często zna go na pamięć
              const exact = offices.find((office) => office.code === (query ?? '').trim());
              const chosen = exact ?? options[active];
              if (chosen) pick(chosen);
            } else if (event.key === 'Escape') {
              close();
            }
          }}
        />
        {selected && (
          <button
            type="button"
            className="btn sec"
            aria-label={t.filing.taxOfficeClear}
            title={t.filing.taxOfficeClear}
            onClick={() => {
              onChange(null);
              setQuery('');
            }}
          >
            ×
          </button>
        )}
      </div>

      {open && place && (
        <ul
          id={listId}
          role="listbox"
          className="combo-list"
          style={{ top: place.top, left: place.left, width: place.width, maxHeight: place.maxHeight }}
        >
          {options.length === 0 && (
            <li className="combo-empty" aria-disabled="true">
              {t.filing.taxOfficeNone}
            </li>
          )}
          {options.map((office, index) => (
            <li
              key={office.code}
              id={`${listId}-${office.code}`}
              role="option"
              aria-selected={index === active}
              className={index === active ? 'combo-item active' : 'combo-item'}
              onMouseDown={(event) => {
                // mousedown, nie click: click przyszedłby już po blur, gdy listy nie ma
                event.preventDefault();
                pick(office);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <span className="mono">{office.code}</span>
              <span className="combo-name">{officeLabel(office.name)}</span>
              {office.specialised && <span className="chip st-warn">{t.filing.taxOfficeSpecialised}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
